import React, {memo, useCallback, useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import classNames from 'classnames';
import {t} from "@i18n";
import {RemotePaging} from "_types/table";
import {Observable} from "@legendapp/state";
import Spinner from "_components/Spinner";
import {PaginatedMeta} from "_types/aspnet";

type PaginationProps = {
    onChangePage?: (page: number) => void;
    onSelectPerPage?: (pageSize: number) => void;
    paginated: PaginatedMeta;
    sizePerPageList: {
        text: string;
        value: number;
    }[];
};

const RemotePagination = ({
    paginated,
    sizePerPageList,
    onChangePage,
    onSelectPerPage
}: PaginationProps) => {
    const {pageIndex, pageSize, totalPages} = paginated;

    const filterPages = useCallback(
        (visiblePages: any, _: number) => {
            return visiblePages.filter((page: any) => page <= totalPages);
        },
        [totalPages]
    );

    const getVisiblePages = useCallback(
        (page: number | null, total: number) => {
            if (total < 7) {
                return filterPages([1, 2, 3, 4, 5, 6], total);
            } else {
                if (page! % 5 >= 0 && page! > 4 && page! + 2 < total) {
                    return [1, page! - 1, page!, page! + 1, total];
                } else if (page! % 5 >= 0 && page! > 4 && page! + 2 >= total) {
                    return [1, total - 3, total - 2, total - 1, total];
                } else {
                    return [1, 2, 3, 4, 5, total];
                }
            }
        },
        [filterPages]
    );

    const changePage = (page: number) => {
        if (page === pageIndex) {
            return;
        }

        const visiblePages = getVisiblePages(page, totalPages);
        setVisiblePages(filterPages(visiblePages, totalPages));


        onChangePage && onChangePage(page);

    };

    const selectPerPage = (e: React.FormEvent<HTMLSelectElement>) => {

        onSelectPerPage && onSelectPerPage(Number(e.currentTarget.value));
        return;
    }

    useEffect(() => {
        const visiblePages = getVisiblePages(null, totalPages);
        setVisiblePages(visiblePages);
    }, [totalPages, getVisiblePages]);

    const [visiblePages, setVisiblePages] = useState<number[]>(getVisiblePages(null, totalPages));
    const activePage: number = pageIndex!;

    if (totalPages === 0) {
        return <></>;
    }

    return (
        <>
            <div className="d-lg-flex align-items-center text-center pb-1">
                <PageSelector {...{sizePerPageList, pageSize, selectPerPage}}/>

                <PageInfo {...{totalPages, pageIndex}}/>

                <Pages {...{visiblePages, activePage, changePage, totalPages}}/>
            </div>
        </>
    );
};

const Pages = memo(({visiblePages, activePage, changePage, totalPages}: {
    visiblePages: number[],
    activePage: number,
    changePage: (page: number) => void,
    totalPages: number
}) => (
    <ul className="pagination pagination-rounded d-inline-flex ms-auto align-item-center mb-0">
        <li
            key="prevpage"
            className={classNames('page-item', 'paginate_button', 'previous', {
                disabled: activePage === 1,
            })}
            onClick={() => {
                if (activePage === 1) return;
                changePage(activePage - 1);
            }}
        >
            <Link to="#" className="page-link">
                <i className="mdi mdi-chevron-left"></i>
            </Link>
        </li>
        {(visiblePages || []).map((page, index, array) => {
            if (array[index - 1] + 1 < page) {
                return <React.Fragment key={page}>
                    <li className="page-item disabled d-none d-xl-inline-block">
                        <Link to="#" className="page-link">
                            ...
                        </Link>
                    </li>
                    <li
                        className={classNames('page-item', 'd-none', 'd-xl-inline-block', {
                            active: activePage === page,
                        })}
                        onClick={(e: React.FormEvent<HTMLLIElement>) => changePage(page)}
                    >
                        <Link to="#" className="page-link">
                            {page}
                        </Link>
                    </li>
                </React.Fragment>;
            }

            return <li
                key={page}
                className={classNames('page-item', 'd-none', 'd-xl-inline-block', {
                    active: activePage === page,
                })}
                onClick={(e: React.FormEvent<HTMLLIElement>) => changePage(page)}
            >
                <Link to="#" className="page-link">
                    {page}
                </Link>
            </li>;
        })}
        <li
            key="nextpage"
            className={classNames('page-item', 'paginate_button', 'next', {
                disabled: activePage === totalPages,
            })}
            onClick={() => {
                if (activePage === totalPages) return;
                changePage(activePage + 1);
            }}
        >
            <Link to="#" className="page-link">
                <i className="mdi mdi-chevron-right"></i>
            </Link>
        </li>
    </ul>
));


const PageInfo = memo(({pageIndex, totalPages}: {
    pageIndex?: number,
    totalPages: number
}) => (
    <span className="me-3">
        {t('Page') + ' '}
        <strong>
            {pageIndex} {t('of')} {totalPages}
        </strong>
        {' '}
    </span>
));

const PageSelector = memo(({sizePerPageList, pageSize, selectPerPage}: {
    sizePerPageList: { text: string, value: number }[],
    pageSize?: number,
    selectPerPage: (e: React.FormEvent<HTMLSelectElement>) => void
}) => {
    if (!sizePerPageList || sizePerPageList.length === 0) {
        return null;
    }

    return (
        <div className="d-inline-block me-3">
            {/*<label className="me-1">Display :</label>*/}
            <select
                value={pageSize}
                onChange={selectPerPage}
                className="form-select d-inline-block w-auto"
            >
                {(sizePerPageList || []).map((pageSize, index) => {
                    return (
                        <option key={index.toString()} value={pageSize.value}>
                            {pageSize.text}
                        </option>
                    );
                })}
            </select>
        </div>
    )
});


export default RemotePagination;
